import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { IconCollection } from "components/Icon/IconCollection";
import PageContent from "components/Layout/PageContent";
import PageHeader from "components/Layout/PageHeader";
import StateTabs from "components/StateTabs";
import RecordTable from "components/Table/RecordTable";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import { getUserData } from "containers/Auth/Auth.selectors";
import { getBundleRepositoriesList } from "containers/BundleRepositories/BundleRepositories.selector";
import {
    readStoredFilter,
    writeStoredFilter,
} from "containers/ExecutionFilter/filterStorage";
import { useQueryParams } from "containers/ExecutionFilter/utils";
import { LiveUpdatesIcon } from "containers/Executions/components/LiveUpdatesIcon";
import { LogListFilter } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/LogListFilter";
import { LogsListing } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/LogsListing";
import { baseLogColumns } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/columns";
import {
    getRecordLogLevelList,
    initialQuery as logsInitialQuery,
} from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/constants";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import useReplaceIdWithName from "utils/useReplaceIdWithName";
import { useHistory, useLocation } from "react-router-dom";
import { ErrorBoundary } from "utils/RouterManager/screens/Error";
import {
    FILTER_QUERY_KEYS,
    parseSearchParams,
    stripFilterFromSearch,
} from "utils/RouterManager/searchParams";
import { getLocalStorageItem, parseJSONSafely, setLocalStorageItem } from "utils/service";
import "./BundleManager.scss";
import { BundleActionToolbar } from "./BundleToolbar";
import { actions as bundleActions } from "./Bundles.reducer";
import { getBundlesData, getBundlesFetching, getDisabledBundlesData } from "./Bundles.selectors";
import { bundleColumns } from "./columns";
import {
    bundleManagerActiveTabStorageKey,
    bundleManagerLogFilterRecordType,
    bundleManagerPaginationStorageKey,
    bundleManagerTableSettingsKey,
    getBundleManagerLogFilterStorageKey,
    importedTabKey,
    updateTabKey,
} from "./constants";
import { BundleResponseData, BundleToolbar } from "./interfaces";
import { fetchBundlesData, getBundleTableData } from "./utils";

export const initialQuery = {
    limit: 10 as 10,
    offset: 0,
    order: "+name",
};

export interface BundleState {
    bundle_key: string;
    operation: "INSTALL" | "UNINSTALL" | "RELEASE";
    state: "STARTED" | "FAILED" | "SUCCEEDED";
}

export const logScreenId = "logsScreen";

const useScrollToHash = () => {
    const scrolledRef = useRef(false);
    const { hash } = useLocation();
    useEffect(() => {
        setTimeout(() => {
            if (hash && !scrolledRef.current) {
                const id = hash.replace("#", "");
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                    scrolledRef.current = true;
                }
            }
        }, 500);
    });
};

const BundleManager = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { queryParams } = useQueryParams();

    const userData = useSelector(getUserData);
    const workspaceId = userData?.workspace_id;
    const workspaceName: string = userData?.workspace_name;
    useReplaceIdWithName(workspaceId, workspaceName);

    const logFilterContext = useMemo(
        () =>
            workspaceId
                ? { recordType: bundleManagerLogFilterRecordType, recordId: workspaceId }
                : undefined,
        [workspaceId]
    );

    const [effectiveQueryParams, setEffectiveQueryParams] = useState(queryParams);

    useLayoutEffect(() => {
        if (!workspaceId) {
            return;
        }

        const filterStorageKey = getBundleManagerLogFilterStorageKey(workspaceId);
        const urlParams = parseSearchParams(history.location.search);
        const hasUrlFilter = FILTER_QUERY_KEYS.some((key) => key in urlParams);

        if (hasUrlFilter && !readStoredFilter(filterStorageKey)) {
            writeStoredFilter(filterStorageKey, {
                filter: urlParams.filter,
                customFilter: urlParams.customFilter,
                include_deleted:
                    urlParams.include_deleted === undefined
                        ? undefined
                        : urlParams.include_deleted === "true",
                hide_deleted:
                    urlParams.hide_deleted === undefined
                        ? undefined
                        : urlParams.hide_deleted === "true",
                tab: urlParams.tab,
            });
        }

        const cleanedSearch = stripFilterFromSearch(history.location.search);
        const currentSearch = history.location.search.startsWith("?")
            ? history.location.search.substring(1)
            : history.location.search;
        if (cleanedSearch !== currentSearch) {
            history.replace({
                pathname: history.location.pathname,
                search: cleanedSearch ? `?${cleanedSearch}` : "",
            });
        }
    }, [history, workspaceId]);
    const bundleState: BundleState[] = useSelector(
        (state: any) => state?.dataStore?.bundle_state ?? []
    );
    const mappedBundleState = useMemo(
        () =>
            bundleState.reduce((acc: { [key: string]: BundleState }, bundleState: BundleState) => {
                acc[bundleState.bundle_key] = bundleState;
                return acc;
            }, {}),
        [bundleState]
    );

    const allTabKey = "all";
    const tabs = [
        { uid: allTabKey, label: "All" },
        { uid: importedTabKey, label: "Imported" },
        { uid: updateTabKey, label: "Updates available" },
    ];

    const [activeTab, setActiveTab] = useState(() => {
        const raw = getLocalStorageItem(bundleManagerActiveTabStorageKey);
        const parsed = parseJSONSafely(raw, null);
        const allowed = new Set([allTabKey, importedTabKey, updateTabKey]);
        return typeof parsed === "string" && allowed.has(parsed) ? parsed : allTabKey;
    });

    useEffect(() => {
        setLocalStorageItem(bundleManagerActiveTabStorageKey, activeTab);
    }, [activeTab]);

    const bundleRepositoriesList = useSelector(getBundleRepositoriesList);
    const bundleRepositoryData = useMemo(() => {
        const bundleRepositoryData: { [id: string]: string } = {};
        for (const bundleRepository of bundleRepositoriesList) {
            bundleRepositoryData[bundleRepository["id"]] = bundleRepository.name;
        }
        return bundleRepositoryData;
    }, [bundleRepositoriesList]);

    const fetchBundles = useCallback(
        () => fetchBundlesData(dispatch, bundleRepositoryData),
        [bundleRepositoryData, dispatch]
    );

    useEffect(() => {
        Object.values(mappedBundleState).forEach((bundle) => {
            if (bundle.state === "STARTED") {
                dispatch({
                    type: bundleActions.DISABLE_BUNDLE,
                    name: bundle.bundle_key,
                });
            } else {
                dispatch({
                    type: bundleActions.ENABLE_BUNDLE,
                    name: bundle.bundle_key,
                });
            }
        });
    }, [dispatch, mappedBundleState]);

    useEffect(() => {
        fetchBundles();
    }, [fetchBundles]);

    const bundleResponseData: BundleResponseData = useSelector(getBundlesData);
    const isFetching = useSelector(getBundlesFetching);

    const bundleTableData = getBundleTableData(
        bundleResponseData,
        bundleRepositoriesList,
        activeTab
    );
    const bundlesCount = bundleTableData?.length || 0;

    const bundleTableInitialQuery = useMemo(
        () => getInitialQueryWithTableSettings(bundleManagerTableSettingsKey, initialQuery),
        []
    );

    const bundleTableClientPagination = useMemo(
        () => ({
            storageKey: bundleManagerPaginationStorageKey,
            segmentKey: activeTab,
        }),
        [activeTab]
    );

    useEffect(() => {
        const isUpdateNeeded = Object.values(mappedBundleState).some(
            ({ state }) => state === "SUCCEEDED"
        );
        if (isUpdateNeeded) {
            fetchBundles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mappedBundleState]);

    const [logsQueryInfo, setLogsQueryInfo] = useState(logsInitialQuery);

    const updateQuery = useCallback(
        (newQuery: any) => {
            setLogsQueryInfo({ ...logsQueryInfo, ...newQuery });
        },
        [logsQueryInfo]
    );

    const initialFilter = useMemo(() => ({ level: getRecordLogLevelList() }), []);
    const additionalQueryField = useMemo(
        () => ({
            recordLogFilter: {
                field: "record_id--record.id:record_type",
                op: "in",
                value: ["BUNDLE_REPOSITORY"],
            },
        }),
        []
    );

    const disabledData = useSelector(getDisabledBundlesData);
    const [isLiveUpdatesEnabled, setLiveUpdatesEnabled] = useState(true);

    useScrollToHash();

    return (
        <>
            <Breadcrumbs breadcrumbs={[{ link: "", label: "bundle manager" }]} />
            <PageHeader
                title={`${workspaceName ?? ""} bundle manager`}
                titleIcon={IconCollection.cartArrowDown}
                subtitle="Search and import bundles from Cloudomation, and choose your update settings. "
            />
            <PageContent className="bundleManager">
                <StateTabs activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs} />
                <RecordTable
                    data={bundleTableData}
                    isLoading={isFetching}
                    totalItemCount={bundlesCount}
                    recordTypes="all"
                    tableSettingsKey={bundleManagerTableSettingsKey}
                    options={{
                        toolbar: "custom",
                        customLeftToolbar: BundleActionToolbar as (props: BundleToolbar) => any,
                        toolbarFilters: false,
                        density: "high",
                        skipFetchOnAction: true,
                        clientSidePaginationPersist: bundleTableClientPagination,
                        isRowSelectable: (dataIndex) => {
                            const bundleKey = bundleTableData[dataIndex].key;
                            return !disabledData[bundleKey];
                        },
                    }}
                    refresh={fetchBundles}
                    initialQuery={bundleTableInitialQuery}
                    columns={bundleColumns}
                />
            </PageContent>
            <PageContent className="bundleManager">
                <PageHeader
                    title="Logs"
                    titleImage={<></>}
                    titleAction={
                        <LiveUpdatesIcon
                            isLiveUpdatesEnabled={isLiveUpdatesEnabled}
                            setLiveUpdatesEnabled={setLiveUpdatesEnabled}
                        />
                    }
                />
                <LogListFilter
                    initialFilter={initialFilter}
                    filterContext={logFilterContext}
                    history={history}
                    queryParams={queryParams}
                    isDisabled={!isLiveUpdatesEnabled}
                    onEffectiveQueryParamsChange={setEffectiveQueryParams}
                />
                <LogsListing
                    resourceName={"bundles"}
                    id={"bundle-manager"}
                    additionalQueryField={additionalQueryField}
                    queryInfo={logsQueryInfo}
                    columns={baseLogColumns}
                    updateQuery={updateQuery}
                    idList={Object.keys(bundleRepositoryData)}
                    history={history}
                    queryParams={effectiveQueryParams}
                    isLiveUpdatesEnabled={isLiveUpdatesEnabled}
                />
            </PageContent>
        </>
    );
};

const WrappedBundleManager = withErrorBoundary(BundleManager, {
    FallbackComponent: ErrorBoundary,
    onError(error) {
        console.error(error);
    },
});

export default WrappedBundleManager;
